import { useEffect, useMemo, useState, useCallback } from "react";
import { X, Calendar, MapPin, Euro, Filter, CreditCard, User as UserIcon, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { todayLocalIso } from "../../lib/dateUtils";
import BookingDetailContent from "./BookingDetailContent";
import RescheduleModal from "../../components/RescheduleModal";
import BookingContactBadge from "../../components/BookingContactBadge";

/**
 * "Histórico de reservas" — área pessoal do cliente autenticado.
 *
 * Três abas:
 *   • Por confirmar — status == "pending"
 *   • Confirmadas   — status em {"confirmed", "in_progress", "completed"}
 *   • Canceladas    — status == "cancelled"
 *
 * Cada linha mostra data, hora, serviço, estado e profissional (se atribuído).
 * Clicar numa linha abre um modal com todos os detalhes da reserva, sem
 * navegar para outra página. Filtros por data, alojamento e serviço.
 * Ordenação: mais recente primeiro (por `created_at` desc, fallback start_at).
 */
const TABS = [
  { key: "pending",   label: "Por confirmar", accent: "#f59e0b", match: (b) => b.status === "pending" },
  { key: "confirmed", label: "Confirmadas",   accent: "#d4af37", match: (b) => ["confirmed", "in_progress", "completed"].includes(b.status) },
  { key: "cancelled", label: "Canceladas",    accent: "#ef4444", match: (b) => b.status === "cancelled" },
];

export default function ClientBookings() {
  const [items, setItems] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [tab, setTab] = useState("confirmed");
  const [apartmentFilter, setApartmentFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(true);

  // Modal state
  const [modalBid, setModalBid] = useState(null);
  const [modalBooking, setModalBooking] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [editing, setEditing] = useState(null); // booking object to edit

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bookings, apts] = await Promise.all([
        api.get("/bookings"),
        api.get("/apartments").catch(() => ({ data: { items: [] } })),
      ]);
      setItems(bookings.data.items || []);
      setApartments(apts.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Fetch full booking when modal opens
  useEffect(() => {
    if (!modalBid) { setModalBooking(null); return; }
    let alive = true;
    setModalLoading(true);
    api.get(`/bookings/${modalBid}`)
      .then(({ data }) => { if (alive) setModalBooking(data); })
      .catch((e) => { if (alive) toast.error(formatApiError(e)); })
      .finally(() => { if (alive) setModalLoading(false); });
    return () => { alive = false; };
  }, [modalBid]);

  // Close on ESC + lock body scroll while modal open
  useEffect(() => {
    if (!modalBid) return;
    const onKey = (e) => { if (e.key === "Escape") setModalBid(null); };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [modalBid]);

  async function cancelFromModal() {
    if (!modalBid || !window.confirm("Cancelar esta reserva?")) return;
    try {
      await api.patch(`/bookings/${modalBid}/status`, { status: "cancelled" });
      toast.success("Reserva cancelada");
      setModalBid(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  function resetFilters() {
    setApartmentFilter("all");
    setServiceFilter("all");
    setDateFrom("");
    setDateTo("");
  }
  const filtersActive = apartmentFilter !== "all" || serviceFilter !== "all" || dateFrom || dateTo;

  const counts = useMemo(() => ({
    pending:   items.filter(TABS[0].match).length,
    confirmed: items.filter(TABS[1].match).length,
    cancelled: items.filter(TABS[2].match).length,
  }), [items]);

  // Distinct service types in the client's bookings (for the filter dropdown)
  const services = useMemo(() => {
    const set = new Set();
    items.forEach((b) => { if (b.apt_type) set.add(b.apt_type); });
    return Array.from(set).sort();
  }, [items]);

  const activeTab = TABS.find((t) => t.key === tab) || TABS[1];
  const visible = useMemo(() => {
    let filt = items.filter(activeTab.match);
    if (apartmentFilter !== "all") filt = filt.filter((b) => b.apartment_id === apartmentFilter);
    if (serviceFilter !== "all")   filt = filt.filter((b) => b.apt_type === serviceFilter);
    if (dateFrom) filt = filt.filter((b) => (b.scheduled_date || "") >= dateFrom);
    if (dateTo)   filt = filt.filter((b) => (b.scheduled_date || "") <= dateTo);

    // Most recent first — by created_at, fallback to start_at/scheduled_date+time
    return [...filt].sort((a, b) => {
      const ka = a.created_at || `${a.scheduled_date} ${a.scheduled_time || ""}`;
      const kb = b.created_at || `${b.scheduled_date} ${b.scheduled_time || ""}`;
      return kb.localeCompare(ka);
    });
  }, [items, activeTab, apartmentFilter, serviceFilter, dateFrom, dateTo]);

  return (
    <div className="space-y-8" data-testid="client-bookings-page">
      <div>
        <div className="eyebrow mb-3">Histórico</div>
        <h1 className="font-display text-4xl sm:text-5xl">Minhas reservas</h1>
        <p className="text-sm text-[#a0b2af] mt-3 max-w-2xl">
          Todas as suas reservas num só lugar, organizadas por estado. Toque numa
          reserva para abrir os detalhes completos sem sair desta página.
        </p>
      </div>

      {counts.pending > 0 && (
        <BookingContactBadge tone="urgent" justCreated={false} />
      )}

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="bookings-tabs">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button key={t.key}
                    onClick={() => setTab(t.key)}
                    className="group relative px-5 py-3 text-xs uppercase tracking-widest rounded-sm transition-all flex items-center gap-2.5 border"
                    style={{
                      borderColor: active ? t.accent : "#1a4640",
                      background: active ? `${t.accent}14` : "transparent",
                      color: active ? t.accent : "#a0b2af",
                    }}
                    data-testid={`bookings-tab-${t.key}`}>
              <span className="w-2 h-2 rounded-full" style={{ background: t.accent }} />
              {t.label}
              <span className="text-[10px] font-mono opacity-80">{counts[t.key]}</span>
            </button>
          );
        })}
      </div>

      {/* Visual separator with active tab accent */}
      <div className="h-px w-full" style={{ background: `linear-gradient(to right, ${activeTab.accent}, transparent)` }} />

      {/* Active-filter chip row (so users know results are filtered even with filters section collapsed at bottom) */}
      {filtersActive && (
        <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest"
             data-testid="bookings-active-filters">
          <span className="text-[#a0b2af]">Filtros ativos:</span>
          {apartmentFilter !== "all" && (
            <Chip>{apartments.find((a) => a.id === apartmentFilter)?.name || "Alojamento"}</Chip>
          )}
          {serviceFilter !== "all" && <Chip>Serviço: {serviceFilter}</Chip>}
          {dateFrom && <Chip>Desde: {dateFrom}</Chip>}
          {dateTo && <Chip>Até: {dateTo}</Chip>}
          <button onClick={resetFilters}
                  className="text-[#d4af37] hover:underline"
                  data-testid="bookings-filter-reset-top">
            Limpar
          </button>
        </div>
      )}

      {/* Results */}
      <div className="space-y-3">
        {loading && <div className="text-[#a0b2af] text-sm">A carregar…</div>}
        {!loading && visible.length === 0 && (
          <EmptyState tab={tab} accent={activeTab.accent} />
        )}
        {visible.map((b) => (
          <BookingRow key={b.id} booking={b} accent={activeTab.accent}
                      onClick={() => setModalBid(b.id)} />
        ))}
      </div>

      {/* Filters (moved to the bottom — users scan the list first, then refine) */}
      {items.length > 0 && (
        <section className="pt-6 border-t border-[#1a4640]" data-testid="bookings-search-section">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={14} className="text-[#d4af37]" />
            <h2 className="font-display text-xl text-[#fdfbf7]">Procurar reservas</h2>
            {filtersActive && (
              <button onClick={resetFilters}
                      className="ml-auto text-[10px] uppercase tracking-widest text-[#d4af37] hover:underline"
                      data-testid="bookings-filter-reset">
                Limpar filtros
              </button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3" data-testid="bookings-filters">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#a0b2af] mb-1.5">
                Alojamento
              </label>
              <select value={apartmentFilter}
                      onChange={(e) => setApartmentFilter(e.target.value)}
                      className="w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none rounded-sm px-3 py-2 text-sm text-[#fdfbf7]"
                      data-testid="bookings-filter-apartment">
                <option value="all">Todos</option>
                {apartments.map((a) => (
                  <option key={a.id} value={a.id}>{a.name || a.address || a.id}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#a0b2af] mb-1.5">
                Serviço
              </label>
              <select value={serviceFilter}
                      onChange={(e) => setServiceFilter(e.target.value)}
                      className="w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none rounded-sm px-3 py-2 text-sm text-[#fdfbf7]"
                      data-testid="bookings-filter-service">
                <option value="all">Todos</option>
                {services.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#a0b2af] mb-1.5">Desde</label>
              <input type="date" value={dateFrom}
                     onChange={(e) => setDateFrom(e.target.value)}
                     className="w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none rounded-sm px-3 py-2 text-sm text-[#fdfbf7]"
                     data-testid="bookings-filter-from" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[#a0b2af] mb-1.5">Até</label>
              <input type="date" value={dateTo}
                     onChange={(e) => setDateTo(e.target.value)}
                     className="w-full bg-[#031c18] border border-[#1a4640] focus:border-[#d4af37] outline-none rounded-sm px-3 py-2 text-sm text-[#fdfbf7]"
                     data-testid="bookings-filter-to" />
            </div>
          </div>
        </section>
      )}

      {/* Modal */}
      {modalBid && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
             data-testid="booking-detail-modal"
             role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"
               onClick={() => setModalBid(null)} />
          <div className="relative w-full max-w-4xl max-h-[92vh] overflow-hidden rounded-sm border border-[#1a4640] bg-[#041f1b] shadow-2xl flex flex-col"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1a4640]">
              <div>
                <div className="eyebrow text-[#d4af37]">Detalhes da reserva</div>
                {modalBooking && (
                  <div className="font-display text-2xl mt-1">
                    {modalBooking.apartment_name || modalBooking.apt_type}
                  </div>
                )}
              </div>
              <button onClick={() => setModalBid(null)}
                      className="w-9 h-9 rounded-sm border border-[#1a4640] text-[#a0b2af] hover:text-[#d4af37] hover:border-[#d4af37] flex items-center justify-center transition-colors"
                      data-testid="modal-close"
                      aria-label="Fechar">
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto px-5 py-6">
              {modalLoading && (
                <div className="flex items-center gap-2 text-[#a0b2af] text-sm">
                  <Loader2 size={14} className="animate-spin" /> A carregar detalhes…
                </div>
              )}
              {!modalLoading && modalBooking && (
                <>
                  {modalBooking.status === "pending" && (
                    <div className="mb-5">
                      <BookingContactBadge tone="urgent" justCreated={false} />
                    </div>
                  )}
                  <BookingDetailContent
                    booking={modalBooking}
                    onCancel={cancelFromModal}
                    onEdit={() => setEditing(modalBooking)} />
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {editing && (
        <RescheduleModal
          booking={editing}
          onClose={() => setEditing(null)}
          onSaved={(fresh) => { setModalBooking(fresh); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function BookingRow({ booking: b, accent, onClick }) {
  const todayStr = todayLocalIso();
  const isOverdue = b.scheduled_date < todayStr
    && ["pending", "confirmed", "in_progress"].includes(b.status);
  const daysLate = isOverdue
    ? Math.max(0, Math.round((new Date(todayStr) - new Date(b.scheduled_date)) / 86400000))
    : 0;
  const overdueRing = isOverdue ? "ring-1 ring-[#ef4444]/50" : "";
  return (
    <button onClick={onClick}
            className={`w-full text-left p-5 rounded-sm border-l-4 transition-all hover:scale-[1.003] hover:ring-1 hover:ring-[#d4af37]/50 cursor-pointer group ${overdueRing}`}
            style={{ borderLeftColor: isOverdue ? "#ef4444" : accent, background: "#062c27" }}
            data-testid={`booking-row-${b.id}`}
            aria-label={`Abrir detalhes da reserva ${b.payment_reference || b.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-[#a0b2af] flex items-center gap-2 flex-wrap">
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
            <span style={{ color: accent }}>{statusLabelForRow(b.status)}</span>
            {isOverdue && (
              <span className="text-[#fca5a5] border border-[#ef4444]/50 px-2 py-0.5 rounded-sm font-semibold"
                    data-testid={`booking-overdue-${b.id}`}>
                ⚠ Atrasada · {daysLate === 0 ? "hoje" : `${daysLate}d`}
              </span>
            )}
            {b.payment_reference && (
              <span className="font-mono normal-case tracking-normal text-[#a0b2af]">
                · {b.payment_reference}
              </span>
            )}
          </div>

          <div className="mt-2 text-lg text-[#fdfbf7] flex items-center gap-2 flex-wrap">
            <Calendar size={15} className="text-[#d4af37]" />
            <span>{b.scheduled_date}</span>
            <span className="text-[#a0b2af]">·</span>
            <span>{b.scheduled_time}</span>
          </div>

          <div className="mt-1.5 text-sm text-[#cde3df] flex items-center gap-2 flex-wrap">
            <span className="uppercase tracking-widest text-[10px] text-[#a0b2af]">Serviço</span>
            <span className="text-[#d4af37]">{b.apartment_name || b.apt_type}</span>
            {b.apartment_name && (<span className="text-[#a0b2af] text-xs">· {b.apt_type}</span>)}
          </div>

          <div className="mt-1.5 text-xs text-[#a0b2af] flex items-center gap-2 flex-wrap">
            <UserIcon size={11} />
            {b.assigned_employee_name
              ? <span>Profissional: <span className="text-[#fdfbf7]">{b.assigned_employee_name}</span></span>
              : <span className="italic">Profissional por atribuir</span>}
          </div>

          {b.entry_address && (
            <div className="text-xs text-[#a0b2af] mt-1.5 flex items-center gap-1.5 truncate">
              <MapPin size={11} /> {renderAddress(b.entry_address)}
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-1.5">
            {b.payment_status && <PaymentChip status={b.payment_status} />}
            {Array.isArray(b.extras) && b.extras.length > 0 && (
              <span className="inline-block px-2 py-0.5 border border-[#d4af37]/40 text-[#d4af37] rounded-sm text-[10px] uppercase tracking-widest">
                {b.extras.length} extra{b.extras.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 shrink-0">
          <div className="text-right">
            <div className="font-display text-base text-[#d4af37] uppercase tracking-widest">
              A combinar
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#d4af37]/70 group-hover:text-[#d4af37] mt-2 flex items-center gap-1 justify-end">
              Ver detalhes <ChevronRight size={11} />
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

function EmptyState({ tab, accent }) {
  const msg = {
    pending:   "Não tem reservas por confirmar.",
    confirmed: "Ainda não tem reservas confirmadas.",
    cancelled: "Sem reservas canceladas — excelente!",
  }[tab];
  return (
    <div className="lux-card p-10 text-center" style={{ borderColor: `${accent}33` }}
         data-testid="bookings-empty">
      <div className="text-[#a0b2af] text-sm">{msg}</div>
      {tab === "pending" && (
        <a href="/cliente/agendar"
           className="inline-block mt-4 btn-gold px-5 py-2 text-xs uppercase tracking-widest"
           data-testid="bookings-empty-cta">
          Agendar limpeza →
        </a>
      )}
    </div>
  );
}

function PaymentChip({ status }) {
  const map = {
    paid:      { label: "Pago",        cls: "border-[#10b981]/50 text-[#10b981]" },
    pending:   { label: "Por pagar",   cls: "border-[#f59e0b]/50 text-[#f59e0b]" },
    pending_payment: { label: "Por pagar", cls: "border-[#f59e0b]/50 text-[#f59e0b]" },
    abandoned: { label: "Abandonado",  cls: "border-[#ef4444]/40 text-[#fca5a5]" },
    refunded:  { label: "Reembolsado", cls: "border-[#64748b]/50 text-[#94a3b8]" },
  };
  const s = map[status] || { label: status, cls: "border-[#1a4640] text-[#a0b2af]" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] uppercase tracking-widest rounded-sm ${s.cls}`}>
      <CreditCard size={10} /> {s.label}
    </span>
  );
}

function renderAddress(a) {
  if (!a) return "";
  if (typeof a === "string") return a;
  const parts = [a.street, a.number, a.postal_code, a.council].filter(Boolean);
  return parts.join(", ");
}

function statusLabelForRow(s) {
  return {
    pending:     "Por confirmar",
    confirmed:   "Confirmada",
    in_progress: "Em curso",
    completed:   "Concluída",
    cancelled:   "Cancelada",
  }[s] || s;
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 border border-[#d4af37]/40 text-[#d4af37] rounded-sm bg-[#d4af37]/5">
      {children}
    </span>
  );
}
